'use client';

import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, FileText } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Constants
const SCROLL_THRESHOLD_PX = 20;
const SECTION_TOP_THRESHOLD_PX = 100;
const INDENT_LEVEL_2_PX = 4;
const INDENT_LEVEL_3_PX = 8;
const ANIMATION_DELAY_FACTOR = 0.05;
const HEADING_LEVEL_2 = 2;
const HEADING_LEVEL_3 = 3;

type TableOfContentsItem = {
  id: string;
  title: string;
  level: number;
};

type LegalPageLayoutProps = {
  title: string;
  description: string;
  lastUpdated: string;
  children: React.ReactNode;
  tableOfContents?: TableOfContentsItem[];
};

export function LegalPageLayout({
  title,
  description,
  lastUpdated,
  children,
  tableOfContents = [],
}: LegalPageLayoutProps) {
  const router = useRouter();
  const t = useTranslations('legalLayout');
  const [activeSection, setActiveSection] = useState<string>('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > SCROLL_THRESHOLD_PX);

      // Update active section based on scroll position
      const sections = tableOfContents.map((item) => ({
        id: item.id,
        element: document.getElementById(item.id),
      }));

      const currentSection = sections.find((section) => {
        if (!section.element) {
          return false;
        }
        const rect = section.element.getBoundingClientRect();
        return rect.top <= SECTION_TOP_THRESHOLD_PX && rect.bottom > SECTION_TOP_THRESHOLD_PX;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tableOfContents]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="cosmic-page min-h-screen">
      <div className="cosmic-page-content">
        {/* Header */}
        <motion.header
          animate={{ opacity: 1, y: 0 }}
          className={cn(
            'sticky top-0 z-40 border-purple-500/20 border-b bg-black/40 backdrop-blur-xl transition-all duration-300',
            isScrolled && 'bg-black/60 backdrop-blur-2xl'
          )}
          initial={{ opacity: 0, y: -20 }}
        >
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Button
                className="text-purple-200 hover:text-purple-100"
                onClick={() => router.back()}
                size="sm"
                variant="ghost"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('back')}
              </Button>

              <div className="flex items-center gap-2 text-purple-200/80">
                <FileText className="h-4 w-4" />
                <span className="text-sm">{t('legalInformation')}</span>
              </div>
            </div>
          </div>
        </motion.header>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="mx-auto max-w-4xl">
            {/* Page Header */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 text-center md:mb-12"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="mb-4 font-bold font-display text-3xl text-white md:text-4xl lg:text-5xl">
                {title}
              </h1>
              <p className="mb-6 text-lg text-purple-200/80 md:text-xl">{description}</p>
              <div className="flex items-center justify-center gap-2 text-purple-300/60">
                <Calendar className="h-4 w-4" />
                <span className="text-sm">{t('lastUpdated', { date: lastUpdated })}</span>
              </div>
            </motion.div>

            <div className="grid gap-8 lg:grid-cols-12">
              {/* Table of Contents */}
              {tableOfContents.length > 0 && (
                <motion.aside
                  animate={{ opacity: 1, x: 0 }}
                  className="lg:col-span-4"
                  initial={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <div className="cosmic-card sticky top-24 p-6">
                    <h2 className="mb-4 font-display font-semibold text-white text-xl">
                      {t('tableOfContents')}
                    </h2>
                    <nav className="space-y-2">
                      {tableOfContents.map((item, index) => (
                        <button
                          className={cn(
                            'block w-full text-left transition-all duration-200 hover:text-purple-300',
                            activeSection === item.id
                              ? 'font-medium text-purple-400'
                              : 'text-purple-200/70',
                            item.level === HEADING_LEVEL_2 && `pl-${INDENT_LEVEL_2_PX}`,
                            item.level === HEADING_LEVEL_3 && `pl-${INDENT_LEVEL_3_PX}`
                          )}
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          style={{
                            animationDelay: `${index * ANIMATION_DELAY_FACTOR}s`,
                          }}
                          type="button"
                        >
                          {item.title}
                        </button>
                      ))}
                    </nav>
                  </div>
                </motion.aside>
              )}

              {/* Content */}
              <motion.main
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  'prose prose-invert max-w-none',
                  tableOfContents.length > 0 ? 'lg:col-span-8' : 'lg:col-span-12'
                )}
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="cosmic-card p-6 md:p-8">{children}</div>
              </motion.main>
            </div>

            {/* Footer */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="mt-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="cosmic-card p-6">
                <p className="mb-4 text-purple-200/80">{t('supportMessage')}</p>
                <Button
                  className="border-purple-500/30 bg-purple-600/20 text-purple-200 hover:bg-purple-600/30"
                  onClick={() => router.push('/profile')}
                  variant="secondary"
                >
                  {t('contactSupport')}
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
